import { downloadBlob } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/downloadBlob'
import { sanitizeFilename } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/sanitizeFilename'
import type { TCsvType } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/createCsv'
import { createCsv } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/createCsv'
import { logAnyDataDiscrepancies } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/onSubmitForm/_utils/logAnyDataDiscrepancies'
import { getBlocksBody } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/onSubmitForm/api-fetch-helpers/getBlocksBody'
import { getIrrigationZonesBody } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/onSubmitForm/api-fetch-helpers/getIrrigationZonesBody'
import { getPointsBody } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/onSubmitForm/api-fetch-helpers/getPointsBody'
import { validateDateRangeAndInterval } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/onSubmitForm/validators/validateDateRangeAndInterval'
import { validatePropertyIdAndFieldAssetIds } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/onSubmitForm/validators/validatePropertyIdAndFieldAssetIds'
import { validateValueTypes } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/onSubmitForm/validators/validateValueTypes'
import { dataExportStore, type TDataExportStoreState } from 'App/data-export/store/dataExportStore'
import { mapAggregationToLabel } from 'App/data-export/store/selectors/_utils/mapAggregationToLabel'
import type { TDataExportValueTypesTimeseriesAggregation } from 'App/data-export/types/from-code-gen/TDataExportValueTypesTimeseriesAggregation'
import { translate } from 'i18n/i18n'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import UAParser from 'ua-parser-js'
import { apiFetch } from 'utils/apiFetch'
import { isSemiosEmployeeOrTester } from 'utils/isSemiosEmployeeOrTester'

export class ValidationError extends Error {}

export class NoDataError extends Error {}

export const onSubmitForm = async () => {
  const state = dataExportStore.getState()

  const errors: TDataExportStoreState['formErrors'] = {
    ...validatePropertyIdAndFieldAssetIds(state),
    ...validateDateRangeAndInterval(state),
    ...validateValueTypes(state),
  }

  const isValid = Object.values(errors).every((x) => x === null)

  dataExportStore.actions.setFormErrors({ ...state.formErrors, ...errors })

  if (!isValid) {
    throw new ValidationError()
  }

  const fieldAssetStoreState = fieldAssetStore.getState()
  const properties = fieldAssetStoreState.properties ?? {}
  const property = properties?.[Number(state.formState.propertyId)]
  const { dateRange, interval, valueTypes, blockIds, points, irrigationZoneEmitterIds } = state.formState

  if (dateRange[0] && dateRange[1] && interval) {
    const data = await apiFetch({
      url: '/values',
      body: {
        dateFrom: dateRange[0],
        dateTo: dateRange[1],
        featureReferer: 'DATA_EXPORT',
        includeSource: false,
        blocks: getBlocksBody({ property, interval, valueTypes, blockIds }),
        points: getPointsBody({ property, interval, valueTypes, lngLats: points }),
        irrigationZones: getIrrigationZonesBody({
          property,
          interval,
          valueTypes,
          irrigationZoneEmitterIds,
        }),
      },
    })

    if (isSemiosEmployeeOrTester()) {
      logAnyDataDiscrepancies(data)
    }

    const csvTypes: ReadonlyArray<TCsvType> = ['blocks', 'irrigationZones', 'points']
    const csvItems: Array<{ filename: string; content: string }> = []

    for (const csvType of csvTypes) {
      const csv = createCsv({
        data,
        property,
        dataExportStoreState: state,
        fieldAssetStoreState,
        type: csvType,
      })

      if (csv) {
        csvItems.push(csv)
      }
    }

    if (csvItems.length === 0) {
      throw new NoDataError()
    }

    const deviceType = new UAParser(window.navigator.userAgent)?.getDevice()?.type
    const isMobileOrTablet = deviceType === 'mobile' || deviceType === 'tablet'

    if (isMobileOrTablet && csvItems.length > 1) {
      /**
       * This is a workaround for dealing with downloading multiple files on iPhone and iPad. We'll zip up
       * the files into one zip file.
       */

      const textSemios = translate.phrases.banyanApp('Semios')
      const textInterval = mapAggregationToLabel[interval as TDataExportValueTypesTimeseriesAggregation]()
      const textValues = translate.phrases.banyanApp('Values')
      const files = csvItems.map((csvItem) => ({ input: csvItem.content, name: csvItem.filename }))

      const filename = sanitizeFilename(
        `${textSemios}-${textInterval}-${textValues}-${property.propertyName}.zip`,
      )

      // Dynamic import the client-zip library since it's only necessary in this situation
      const downloadZip = await import('client-zip').then((module) => module.downloadZip)

      downloadBlob({
        blob: await downloadZip(files).blob(),
        filename,
      })
    } else {
      for (const [index, csvItem] of csvItems.entries()) {
        if (index > 0) {
          /**
           * This delay logic is to work around an issue seen with Safari where it won't download rapid,
           * successive downloads triggered programmatically using the technique we do in
           * `createAndDownloadCsv.ts`. Adding a small delay between downloads fixes the issue.
           */
          await new Promise((res) => setTimeout(res, 250))
        }

        downloadBlob({
          blob: new Blob([csvItem.content], { type: 'text/csv;charset=utf-8;' }),
          filename: csvItem.filename,
        })
      }
    }
  }
}
